import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './Signup.module.css';
import Footer from '../../common/UI/Footer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

// Initialize Firebase
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGE_SENDING_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase app
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const navigate = useNavigate();
  const { signup, isLoading, error } = useAuth();

  // Show error toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error?.message || 'An error occurred during sign up');
    }
  }, [error]);

  useEffect(() => {
    if (googleError) {
      toast.error(googleError);
    }
  }, [googleError]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }
    
    return newErrors;
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({
      ...prev,
      role
    }));
    // Clear role error when user selects a role
    if (errors.role) {
      setErrors(prev => ({
        ...prev,
        role: ''
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      signup(
        formData,
        {
          onSuccess: (data) => {
            toast.success('Signup successful! Redirecting to login...');
            setTimeout(() => {
              navigate('/');
            }, 1000);
          },
          onError: (error) => {
            const errorMessage = error?.message || 'Failed to sign up';
            toast.error(errorMessage);
            setErrors({ submit: errorMessage });
          }
        }
      );
    } else {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setGoogleError(null);
      
      const result = await signInWithPopup(auth, googleProvider);
      // Get the ID token from the user credential
      const idToken = await result.user.getIdToken();
      
      // Send the token to your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to authenticate with Google');
      }

      const data = await response.json();
      toast.success('Google login successful! Redirecting...');
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Ensure auth state is refreshed
      }, 1000);
    } catch (error) {
      console.error('Google auth error:', error);
      setGoogleError(error.message || 'Failed to authenticate with Google');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className={styles.container}>
        <div className={styles.formCard}>
          <h2>Get Started Now</h2>
          <form onSubmit={handleSubmit} className={styles.form}>

            <div className={styles.roleButtons}>
              <button
                type="button"
                className={`${styles.roleButton} ${formData.role === 'user' ? styles.active : ''}`}
                onClick={() => handleRoleSelect('user')}
                disabled={isLoading}
              >
                User
              </button>
              <button
                type="button"
                className={`${styles.roleButton} ${formData.role === 'admin' ? styles.active : ''}`}
                onClick={() => handleRoleSelect('admin')}
                disabled={isLoading}
              >
                Author
              </button>
            </div>
            {errors.role && <span className={styles.errorText}>{errors.role}</span>}
            
            <div className={styles.inputGroup}>
              <input
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? styles.errorInput : ''}
                disabled={isLoading}
              />
              {errors.name && <span className={styles.errorText}>{errors.name}</span>}
            </div>

            <div className={styles.inputGroup}>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? styles.errorInput : ''}
                disabled={isLoading}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <div className={styles.inputGroup}>
              <div className={styles.passwordInput}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? styles.errorInput : ''}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className={styles.eyeIcon}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <div className={styles.checkboxGroup}>
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                id="terms"
                disabled={isLoading}
              />
              <label htmlFor="terms">I agree to the terms & policy</label>
              {errors.agreeToTerms && <span className={styles.errorText}>{errors.agreeToTerms}</span>}
            </div>

            <button 
              type="submit" 
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.loadingWrapper}>
                  <Loader2 className={styles.spinner} size={20} />
                  Signing up...
                </span>
              ) : (
                'Signup'
              )}
            </button>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <div className={styles.socialButtons}>
              <button 
                type="button" 
                className={styles.googleButton}
                disabled={isLoading || isGoogleLoading}
                onClick={handleGoogleSignIn}
              >
                {isGoogleLoading ? (
                  <span className={styles.loadingWrapper}>
                    <Loader2 className={styles.spinner} size={20} />
                    Connecting...
                  </span>
                ) : (
                  <>
                    <img src="./images/logo/googlelogo.png" className={styles.logoImage} alt="google" />
                    Sign in with Google
                  </>
                )}
              </button>
              <button 
                type="button" 
                className={styles.appleButton}
                disabled={isLoading}
              >
                <img src="./images/logo/fb.png" className={styles.logoappleImage} alt="google" />
                Sign in with Facebook
              </button>
            </div>

            <p className={styles.loginLink}>
              Already have an account? <span style={{ color:'red', cursor:'pointer' }} onClick={()=>navigate('/signin')}>Sign in</span>
            </p>
          </form>
        </div>
      </div>
      <Footer/>
    </>
  );
};

export default Signup;